/**
 * Cue → node-graph recipes (plan 08 A2). 100 % procedural, zero asset bytes: noise comes from one
 * cached white-noise AudioBuffer, tones from oscillators, colour from BiquadFilters, lateral
 * position from StereoPannerNode. Every source registers with a VoicePool so a 48-soldier frame
 * can never spawn unbounded nodes — the pool steals the oldest voice at its cap.
 */
import type { Cue, UiSound } from './cues';
import { motifNotes } from './motif';
import type { Rng } from './rng';

/** Concurrent voices before the oldest is stolen (plan 08: ~24). */
export const MAX_VOICES = 24;

/** One scheduled sound source plus the nodes it must disconnect when done/stolen. */
interface Voice {
  source: AudioScheduledSourceNode;
  nodes: AudioNode[];
}

/**
 * Voice budget for one context. Voices are removed by their source's `onended`; when the cap is hit
 * the OLDEST is stolen (stopped at once + disconnected), so late cues are cut before new ones are
 * dropped — a dropped cue is silence, a stolen one just loses its tail.
 * audit #19: the doc used to promise self-expiry from an `endsAt` field that was written and never
 * read — expiry actually comes from the `onended` handler below (the field is gone with it).
 */
export class VoicePool {
  private readonly voices: Voice[] = [];

  register(source: AudioScheduledSourceNode, nodes: AudioNode[]): void {
    while (this.voices.length >= MAX_VOICES) this.steal();
    const voice: Voice = { source, nodes };
    this.voices.push(voice);
    source.onended = () => {
      const i = this.voices.indexOf(voice);
      if (i >= 0) this.voices.splice(i, 1);
      this.disconnect(voice);
    };
  }

  private steal(): void {
    // Oldest by start time ≈ array order; stop(now) + disconnect releases the graph.
    const oldest = this.voices.shift();
    if (!oldest) return;
    try {
      oldest.source.onended = null;
      oldest.source.stop();
    } catch {
      /* already stopped */
    }
    this.disconnect(oldest);
  }

  private disconnect(voice: Voice): void {
    for (const n of voice.nodes) n.disconnect();
  }

  /** Stop everything immediately (mute / teardown). */
  clear(): void {
    while (this.voices.length > 0) this.steal();
  }
}

// One shared 2 s white-noise buffer per context: the noise sources are all cheap read-throughs.
const noiseBuffers = new WeakMap<BaseAudioContext, AudioBuffer>();

function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let buf = noiseBuffers.get(ctx);
  if (!buf) {
    buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 2), ctx.sampleRate);
    const data = buf.getChannelData(0);
    // Deterministic fill (LCG) — the sim is RNG-free and the noise floor shouldn't be either.
    let s = 0x2f6e2b1;
    for (let i = 0; i < data.length; i++) {
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      data[i] = (s / 0x7fffffff) * 2 - 1;
    }
    noiseBuffers.set(ctx, buf);
  }
  return buf;
}

/** Attack-decay envelope on a fresh gain node (exponential to near-silence, never 0), fed into `dest`. */
function env(ctx: BaseAudioContext, dest: AudioNode, when: number, peak: number, attack: number, decay: number): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, when);
  g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), when + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, when + attack + decay);
  g.connect(dest);
  return g;
}

/**
 * Per-component panner: each voice owns (and disconnects) its own StereoPannerNode, so stealing
 * one voice never cuts the others mid-play. pan 0 skips the node entirely.
 */
function route(ctx: BaseAudioContext, dest: AudioNode, pan: number, nodes: AudioNode[]): AudioNode {
  if (!pan) return dest;
  const p = ctx.createStereoPanner();
  p.pan.value = Math.max(-1, Math.min(1, pan));
  p.connect(dest);
  nodes.push(p);
  return p;
}

export function tone(
  ctx: BaseAudioContext,
  dest: AudioNode,
  voices: VoicePool,
  when: number,
  opts: { freq: number; freqTo?: number; type?: OscillatorType; peak: number; attack?: number; decay: number; detune?: number; pan?: number },
): void {
  const nodes: AudioNode[] = [];
  const out = route(ctx, dest, opts.pan ?? 0, nodes);
  const osc = ctx.createOscillator();
  osc.type = opts.type ?? 'sine';
  osc.frequency.setValueAtTime(opts.freq, when);
  if (opts.freqTo !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(opts.freqTo, 1), when + (opts.attack ?? 0.005) + opts.decay);
  if (opts.detune) osc.detune.value = opts.detune;
  const g = env(ctx, out, when, opts.peak, opts.attack ?? 0.005, opts.decay);
  osc.connect(g);
  nodes.push(osc, g);
  osc.start(when);
  osc.stop(when + opts.decay + (opts.attack ?? 0.005) + 0.02);
  voices.register(osc, nodes);
}

function noise(
  ctx: BaseAudioContext,
  dest: AudioNode,
  voices: VoicePool,
  when: number,
  opts: { peak: number; decay: number; attack?: number; filter?: BiquadFilterType; freq?: number; freqTo?: number; q?: number; pan?: number },
): void {
  const nodes: AudioNode[] = [];
  const out = route(ctx, dest, opts.pan ?? 0, nodes);
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx);
  src.loop = true;
  nodes.push(src);
  let tail: AudioNode = src;
  if (opts.filter && opts.freq !== undefined) {
    const f = ctx.createBiquadFilter();
    f.type = opts.filter;
    f.frequency.setValueAtTime(opts.freq, when);
    if (opts.freqTo !== undefined) f.frequency.exponentialRampToValueAtTime(Math.max(opts.freqTo, 10), when + opts.decay);
    f.Q.value = opts.q ?? 1;
    tail.connect(f);
    tail = f;
    nodes.push(f);
  }

  const g = env(ctx, out, when, opts.peak, opts.attack ?? 0.003, opts.decay);
  tail.connect(g);
  src.start(when, Math.abs(when * 1000) % 1.5); // desynchronise overlapping bursts
  src.stop(when + opts.decay + (opts.attack ?? 0.003) + 0.02);
  voices.register(src, [...nodes, g]);
}

const toneVoice = tone;
const noiseVoice = noise;

export interface SfxVariation {
  pitch: number;
  velocity: number;
  volleyHz: number;
  enemyKillHz: number;
  crateHitHz: number;
}

export function sfxVariation(rng: Rng): SfxVariation {
  const variants = <T,>(values: readonly T[]): T => values[Math.floor(rng() * values.length)]!;
  return {
    pitch: 0.97 + rng() * 0.06,
    velocity: 0.85 + rng() * 0.3,
    volleyHz: variants([1300, 1500, 1750]),
    enemyKillHz: variants([420, 560]),
    crateHitHz: variants([760, 900]),
  };
}

/** Pentatonic scale (A minor: A C D E G) — quantised ticks always sit inside the drone's key. */
const PENTATONIC = [1, 1.1892, 1.3348, 1.4983, 1.7818];

export function uiSoundDuration(sound: UiSound): number {
  return sound === 'operationComplete' ? 0.95 : sound === 'tierComplete' ? 0.71 : sound === 'deploy' ? 0.64 : sound === 'dispatch' ? 0.31 : 0.24;
}

/** Play one cue. `when` is an absolute ctx.currentTime schedule time; `dest` is the sfx bus. */
export function playCue(
  ctx: BaseAudioContext,
  dest: AudioNode,
  cue: Cue,
  when: number,
  voices: VoicePool,
  rng: Rng = () => 0.5,
  rootHz = 220,
): void {
  const variation = sfxVariation(rng);
  const pitched = cue.type === 'gateTick' || cue.type === 'gatePass' || cue.type === 'weaponUp' || cue.type === 'won' || cue.type === 'lost' || cue.type === 'ui';
  const tone = (
    context: BaseAudioContext,
    output: AudioNode,
    pool: VoicePool,
    at: number,
    opts: Parameters<typeof toneVoice>[4],
  ): void =>
    toneVoice(context, output, pool, at, {
      ...opts,
      freq: opts.freq * (pitched ? 1 : variation.pitch),
      freqTo: opts.freqTo === undefined ? undefined : opts.freqTo * (pitched ? 1 : variation.pitch),
      peak: opts.peak * variation.velocity,
    });
  const noise = (
    context: BaseAudioContext,
    output: AudioNode,
    pool: VoicePool,
    at: number,
    opts: Parameters<typeof noiseVoice>[4],
  ): void =>
    noiseVoice(context, output, pool, at, {
      ...opts,
      freq: opts.freq === undefined ? undefined : opts.freq * variation.pitch,
      freqTo: opts.freqTo === undefined ? undefined : opts.freqTo * variation.pitch,
      peak: opts.peak * variation.velocity,
    });
  // One pan value for the whole composite cue; each component owns its panner (see route()).
  const pan = 'pan' in cue ? cue.pan : 0;

  switch (cue.type) {
    case 'volley': {
      // Gain ∝ log2(1 + shown): 48 soldiers sound like a crowd, not 48× louder. One voice/volley.
      const gain = 0.12 * Math.log2(1 + Math.max(1, cue.shown));
      const tier = Math.max(0, cue.tier);
      // Crack: bandpass noise burst; Thump: 90 Hz sine.
      noise(ctx, dest, voices, when, { peak: gain, decay: 0.04, filter: 'bandpass', freq: variation.volleyHz + tier * 500, q: 1.2, pan });
      tone(ctx, dest, voices, when, { freq: 90, freqTo: 55, peak: gain * 0.9, decay: 0.03, pan });
      if (tier >= 1) {
        // Lower body for rifle+ (wider, more mass behind the crack).
        noise(ctx, dest, voices, when, { peak: gain * 0.5, decay: 0.07, filter: 'lowpass', freq: 400, q: 0.7, pan });
      }
      if (tier >= 3) {
        // Bazooka: whoosh then a delayed boom (fuse + detonation).
        noise(ctx, dest, voices, when, { peak: 0.12, decay: 0.18, filter: 'bandpass', freq: 600, freqTo: 150, q: 0.6, pan });
        noise(ctx, dest, voices, when + 0.14, { peak: 0.3, decay: 0.3, filter: 'lowpass', freq: 220, q: 0.5, pan });
        tone(ctx, dest, voices, when + 0.14, { freq: 60, freqTo: 28, peak: 0.35, decay: 0.35, pan });
      }
      break;
    }
    case 'gateTick': {
      // Pitch rises with the live value, quantised to the pentatonic; short and quiet (it fires per bullet hit).
      const idx = ((Math.round(cue.value) % PENTATONIC.length) + PENTATONIC.length) % PENTATONIC.length;
      const base = rootHz * PENTATONIC[idx] * (Math.abs(cue.value) > 30 ? 2 : 1);
      tone(ctx, dest, voices, when, { freq: base, peak: 0.06, decay: 0.06, type: 'triangle', pan });
      break;
    }
    case 'gatePass': {
      noise(ctx, dest, voices, when, { peak: 0.12, decay: 0.2, filter: 'bandpass', freq: 500, freqTo: 3000, q: 0.8, pan }); // whoosh
      if (cue.good) {
        for (let i = 0; i < 3; i++) tone(ctx, dest, voices, when + 0.05 + i * 0.07, { freq: rootHz * PENTATONIC[i + 1], peak: 0.1, decay: 0.18, type: 'triangle', pan }); // major arpeggio
      } else {
        tone(ctx, dest, voices, when + 0.05, { freq: rootHz, peak: 0.11, decay: 0.3, type: 'sawtooth', pan }); // minor-second dissonance
        tone(ctx, dest, voices, when + 0.05, { freq: rootHz * 1.05946, peak: 0.11, decay: 0.3, type: 'sawtooth', pan });
      }
      break;
    }
    case 'crateHit': {
      // Wood knock: high resonant bandpass noise, very short.
      noise(ctx, dest, voices, when, { peak: 0.18, decay: 0.05, filter: 'bandpass', freq: variation.crateHitHz, q: 8, pan });
      tone(ctx, dest, voices, when, { freq: 180, freqTo: 120, peak: 0.08, decay: 0.05, pan });
      break;
    }
    case 'crateBreak': {
      // Crunch: 3 staggered knocks + a low thud.
      for (let i = 0; i < 3; i++) {
        noise(ctx, dest, voices, when + i * 0.035, { peak: 0.16 - i * 0.03, decay: 0.05, filter: 'bandpass', freq: 800 + i * 350, q: 6, pan });
      }
      tone(ctx, dest, voices, when + 0.08, { freq: 90, freqTo: 45, peak: 0.2, decay: 0.15, pan });
      break;
    }
    case 'metalHit': {
      noise(ctx, dest, voices, when, { peak: 0.11, decay: 0.06, filter: 'bandpass', freq: 2200, q: 3, pan });
      tone(ctx, dest, voices, when, { freq: 920, freqTo: 780, type: 'triangle', peak: 0.065, decay: 0.12, pan });
      break;
    }
    case 'metalBreak': {
      for (let i = 0; i < 3; i++) {
        noise(ctx, dest, voices, when + i * 0.055, { peak: 0.13 - i * 0.025, decay: 0.13, filter: 'bandpass', freq: 1800 + i * 430, q: 2, pan });
        tone(ctx, dest, voices, when + i * 0.055, { freq: 760 + i * 210, type: 'triangle', peak: 0.045, decay: 0.16, pan });
      }
      break;
    }
    case 'weaponUp': {
      // Rising saw sweep, 300 ms; higher tier = higher landing pitch.
      const top = rootHz * 2 * Math.pow(2, Math.min(4, Math.max(0, cue.tier)) / 12);
      tone(ctx, dest, voices, when, { freq: rootHz, freqTo: top, type: 'sawtooth', peak: 0.14, attack: 0.05, decay: 0.3, pan });
      break;
    }
    case 'enemyKill': {
      // Muffled pop (rate-limited upstream in CueMapper to ≤ 12/s).
      noise(ctx, dest, voices, when, { peak: 0.14, decay: 0.06, filter: 'lowpass', freq: variation.enemyKillHz, q: 1, pan });
      tone(ctx, dest, voices, when, { freq: 110, freqTo: 60, peak: 0.1, decay: 0.05, pan });
      break;
    }
    case 'loss': {
      // Low impact + falling "squad loss" tone, gain ∝ lost (clamped: one wipe of 100 ≠ 100 pops).
      const gain = 0.1 + 0.06 * Math.log2(1 + Math.min(Math.max(cue.lost, 1), 64));
      tone(ctx, dest, voices, when, { freq: 70, freqTo: 40, peak: gain, decay: 0.15, pan });
      tone(ctx, dest, voices, when + 0.02, { freq: 440, freqTo: 110, type: 'sawtooth', peak: gain * 0.5, decay: 0.35, pan });
      break;
    }
    case 'slamTelegraph': {
      // Rising filtered rumble across the 1.3 s telegraph window.
      noise(ctx, dest, voices, when, { peak: 0.14, attack: 0.9, decay: 0.4, filter: 'lowpass', freq: 60, freqTo: 260, q: 2, pan });
      for (let i = 0; i < 3; i++) {
        tone(ctx, dest, voices, when + i * 0.25, { freq: 720 + i * 90, type: 'triangle', peak: 0.07, decay: 0.12, pan });
        tone(ctx, dest, voices, when + i * 0.25, { freq: 720 + i * 90, type: 'triangle', peak: 0.035, decay: 0.12, pan: 0 });
      }
      break;
    }
    case 'explosion': {
      noise(ctx, dest, voices, when, { peak: 0.2, decay: 0.3, filter: 'bandpass', freq: 1100, freqTo: 250, q: 0.7, pan });
      tone(ctx, dest, voices, when, { freq: 95, freqTo: 35, peak: 0.26, decay: 0.35, pan });
      break;
    }
    case 'slamImpact': {
      // Sub boom + crash; harder on hit. A barrel blast (plan 09) is the miss-strength boom.
      const hit = cue.hit;
      const k = hit ? 1 : 0.55;
      tone(ctx, dest, voices, when, { freq: 45, freqTo: 20, peak: 0.5 * k, attack: 0.004, decay: 0.5, pan });
      noise(ctx, dest, voices, when, { peak: 0.35 * k, decay: hit ? 0.4 : 0.2, filter: 'lowpass', freq: 300, q: 0.7, pan });
      break;
    }
    case 'cannon': {
      // Plan 15: a tank main gun — sharper and much deeper than a volley: mid crack, wide low
      // body, and a long sub tail that sells the calibre (dossier §3 layering, audio-sized).
      noise(ctx, dest, voices, when, { peak: 0.22, decay: 0.05, filter: 'bandpass', freq: 1400, q: 1, pan });
      noise(ctx, dest, voices, when, { peak: 0.3, decay: 0.12, filter: 'lowpass', freq: 900, freqTo: 120, q: 0.8, pan });
      tone(ctx, dest, voices, when, { freq: 55, freqTo: 26, peak: 0.4, attack: 0.004, decay: 0.4, pan });
      break;
    }
    case 'rocket': {
      noise(ctx, dest, voices, when, { peak: 0.18, attack: 0.025, decay: 0.32, filter: 'bandpass', freq: 2000, freqTo: 400, q: 0.7, pan });
      tone(ctx, dest, voices, when, { freq: 240, freqTo: 90, type: 'sawtooth', peak: 0.08, decay: 0.22, pan });
      break;
    }
    case 'shellImpact': {
      // The shell's detonation: BIGGER than the brute slam — longer sub swell, heavier crash and
      // a delayed debris crack, hit-strength (the landed-slam rule is the sim's, not audio's).
      const k = cue.hit ? 1 : 0.6;
      tone(ctx, dest, voices, when, { freq: 38, freqTo: 16, peak: 0.55 * k, attack: 0.006, decay: 0.9, pan });
      noise(ctx, dest, voices, when, { peak: 0.4 * k, decay: 0.7, filter: 'lowpass', freq: 240, q: 0.6, pan });
      noise(ctx, dest, voices, when + 0.12, { peak: 0.18 * k, decay: 0.5, filter: 'bandpass', freq: 700, freqTo: 200, q: 0.9, pan });
      break;
    }
    case 'bossHit': {
      // Metallic FM ping (rate-limited ≤ 8/s): carrier + non-integer modulator ratio through gain.
      const carrier = ctx.createOscillator();
      carrier.type = 'sine';
      carrier.frequency.value = 1400 * variation.pitch;
      const mod = ctx.createOscillator();
      mod.type = 'sine';
      mod.frequency.value = 1400 * variation.pitch * 2.71; // inharmonic ratio = clang
      const modGain = ctx.createGain();
      modGain.gain.value = 900 * variation.pitch;
      mod.connect(modGain);
      modGain.connect(carrier.frequency);
      const carrierNodes: AudioNode[] = [carrier, mod, modGain];
      const g = env(ctx, route(ctx, dest, pan, carrierNodes), when, 0.12 * variation.velocity, 0.003, 0.12);
      carrier.connect(g);
      carrier.start(when);
      mod.start(when);
      carrier.stop(when + 0.15);
      mod.stop(when + 0.15);
      voices.register(carrier, [...carrierNodes, g]);
      break;
    }
    case 'ui': {
      if (cue.sound === 'deploy' || cue.sound === 'tierComplete' || cue.sound === 'operationComplete') {
        const variant = cue.sound === 'deploy' ? 'deploy' : 'success';
        for (const note of motifNotes(rootHz, variant)) {
          tone(ctx, dest, voices, when + note.offset, { freq: note.frequency, type: 'triangle', peak: note.gain * 0.55, attack: 0.01, decay: note.duration });
        }
        if (cue.sound === 'operationComplete') {
          for (const ratio of [1, 1.2599, 1.4983]) {
            tone(ctx, dest, voices, when + 0.55, { freq: rootHz * ratio, type: 'triangle', peak: 0.045, attack: 0.01, decay: 0.35 });
          }
        }
      } else {
        const notes = cue.sound === 'confirm' ? [1, 1.2599] : [1, 1.4983, 2];
        notes.forEach((ratio, i) => tone(ctx, dest, voices, when + i * 0.07, { freq: rootHz * ratio * 2, type: 'triangle', peak: 0.065, decay: 0.13 }));
      }
      break;
    }
    case 'won': {
      // Major brass-like chord, 1.2 s.
      for (const f of [1, 1.2599, 1.4983, 2].map((n) => n * rootHz)) {
        tone(ctx, dest, voices, when, { freq: f, type: 'sawtooth', peak: 0.09, attack: 0.04, decay: 1.2, pan });
      }
      break;
    }
    case 'lost': {
      // Minor chord, sagging pitch.
      for (const f of [1, 1.1892, 1.4983].map((n) => n * rootHz)) {
        tone(ctx, dest, voices, when, { freq: f, freqTo: f * 0.94, type: 'sawtooth', peak: 0.1, attack: 0.05, decay: 1.5, pan });
      }
      break;
    }
  }
}
